import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Select, MenuItem, FormControl, InputLabel,
    Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Alert
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
    fetchOrganizations,
    fetchClipsByOrganization,
    fetchSubtitleSrtForClip,
    uploadSubtitle,
    deleteSubtitle
} from '../api/apiService';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import UploadIcon from '@mui/icons-material/Upload';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

const SubtitlePage = () => {
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrg, setSelectedOrg] = useState('');
    const [clips, setClips] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    
    // 分页状态
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
    const [totalRows, setTotalRows] = useState(0);

    // 弹窗状态
    const [dialogOpen, setDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState('upload');
    const [currentClip, setCurrentClip] = useState(null);
    const [srtContent, setSrtContent] = useState('');
    const [submissionStatus, setSubmissionStatus] = useState({ msg: '', type: '' });

    // 删除确认弹窗
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [clipToDelete, setClipToDelete] = useState(null);

    useEffect(() => {
        const fetchOrgs = async () => {
            try {
                const data = await fetchOrganizations();
                setOrganizations(data);
                if (data.length > 0) {
                    setSelectedOrg(data[0].id);
                }
            } catch (err) { setError('无法加载社团列表'); }
        };
        fetchOrgs();
    }, []);

    // 加载 clips 列表（使用服务端分页）
    const refreshData = useCallback(async () => {
        if (!selectedOrg) return;
        setLoading(true);
        setError('');
        try {
            const clipsResponse = await fetchClipsByOrganization(selectedOrg, {
                page: paginationModel.page + 1, // 后端页码从1开始
                size: paginationModel.pageSize
            });
            const clipsData = clipsResponse.clips || [];
            setClips(clipsData);
            setTotalRows(clipsResponse.total || 0);
        } catch (err) {
            setError(`加载数据失败: ${err.message}`);
            setClips([]);
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    }, [selectedOrg, paginationModel]);

    useEffect(() => {
        refreshData();
    }, [refreshData]);

    // 切换社团时重置分页
    useEffect(() => {
        setPaginationModel({ page: 0, pageSize: 20 });
    }, [selectedOrg]);

    const handleOpenUploadDialog = (clip) => {
        setDialogMode('upload');
        setCurrentClip(clip);
        setSrtContent('');
        setSubmissionStatus({ msg: '', type: '' });
        setDialogOpen(true);
    };
    
    const handleOpenEditDialog = (clip) => {
        setDialogMode('edit');
        setCurrentClip(clip);
        setSrtContent('加载中...');
        setSubmissionStatus({ msg: '', type: '' });
        setDialogOpen(true);

        fetchSubtitleSrtForClip(clip.id).then(srt => {
            setSrtContent(srt || '');
        }).catch(() => setSrtContent('无法加载字幕内容'));
    };

    const handleConfirmSubmit = async () => {
        if (!srtContent.trim()) {
            setSubmissionStatus({ msg: '字幕内容不能为空！', type: 'error' });
            return;
        }
        const action = dialogMode === 'upload' ? '上传' : '更新';
        setSubmissionStatus({ msg: `${action}中...`, type: 'info' });
        try {
            await uploadSubtitle(currentClip.id, srtContent);
            setSubmissionStatus({ msg: `${action}成功！`, type: 'success' });
            setClips(prev => prev.map(clip => (
                clip.id === currentClip.id
                    ? { ...clip, hasSubtitle: true, subtitleLineCount: Math.max(clip.subtitleLineCount || 0, 1) }
                    : clip
            )));
            setTimeout(() => {
                setDialogOpen(false);
            }, 1500);
        } catch (err) {
            setSubmissionStatus({ msg: `${action}失败: ${err.message}`, type: 'error' });
        }
    };
    
    const handleOpenDeleteDialog = (clip) => {
        setClipToDelete(clip);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await deleteSubtitle(clipToDelete.id);
            setClips(prev => prev.map(clip => (
                clip.id === clipToDelete.id
                    ? { ...clip, hasSubtitle: false, subtitleLineCount: 0 }
                    : clip
            )));
            setDeleteDialogOpen(false);
            setClipToDelete(null);
        } catch (err) {
            alert(`删除失败: ${err.message}`);
        }
    };

    const columns = [
        { field: 'id', headerName: 'Clip ID', width: 100 },
        { field: 'title', headerName: '标题', flex: 1, minWidth: 300 },
        { field: 'part', headerName: 'Part', width: 80 },
        {
            field: 'hasSubtitle',
            headerName: '有无字幕',
            width: 150,
            renderCell: (params) => {
                const status = params.row.hasSubtitle;
                return status ? (
                    <Chip icon={<CheckCircleIcon />} label="有字幕" color="success" size="small" variant="outlined" />
                ) : (
                    <Chip icon={<CancelIcon />} label="无字幕" color="default" size="small" variant="outlined" />
                );
            },
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: '操作',
            width: 150,
            getActions: ({ row }) => {
                const status = row.hasSubtitle;
                if (status) {
                    return [
                        <GridActionsCellItem
                            icon={<EditIcon />}
                            label="Edit Subtitle"
                            onClick={() => handleOpenEditDialog(row)}
                        />,
                        <GridActionsCellItem
                            icon={<DeleteIcon />}
                            label="Delete Subtitle"
                            onClick={() => handleOpenDeleteDialog(row)}
                        />,
                    ];
                } else {
                    return [
                        <GridActionsCellItem
                            icon={<UploadIcon />}
                            label="Upload Subtitle"
                            onClick={() => handleOpenUploadDialog(row)}
                        />,
                    ];
                }
            },
        },
    ];

    return (
        <Box sx={{ p: 3, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h4" gutterBottom>字幕管理</Typography>
            
            <FormControl sx={{ mb: 2, minWidth: 240, alignSelf: 'flex-start' }}>
                <InputLabel id="org-select-label">选择社团</InputLabel>
                <Select
                    labelId="org-select-label"
                    value={selectedOrg}
                    label="选择社团"
                    onChange={(e) => setSelectedOrg(e.target.value)}
                >
                    {organizations.map((org) => (
                        <MenuItem key={org.id} value={org.id}>{org.name}</MenuItem>
                    ))}
                </Select>
            </FormControl>

            {error ? <Typography color="error">{error}</Typography> : (
                <Box sx={{ flexGrow: 1, width: '100%' }}>
                    <DataGrid
                        rows={clips}
                        columns={columns}
                        loading={loading}
                        paginationMode="server"
                        rowCount={totalRows}
                        paginationModel={paginationModel}
                        onPaginationModelChange={setPaginationModel}
                        pageSizeOptions={[10, 20, 50]}
                        initialState={{
                            sorting: { sortModel: [{ field: 'id', sort: 'desc' }] },
                        }}
                    />
                </Box>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>
                    {dialogMode === 'upload' ? '上传字幕' : '编辑字幕'} - Clip ID: {currentClip?.id}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" gutterBottom>
                        请将SRT格式的字幕内容粘贴到下面的文本框中。
                    </Typography>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="SRT 字幕内容"
                        type="text"
                        fullWidth
                        multiline
                        rows={15}
                        value={srtContent}
                        onChange={(e) => setSrtContent(e.target.value)}
                        variant="outlined"
                    />
                    {submissionStatus.msg && (
                        <Alert severity={submissionStatus.type} sx={{ mt: 2 }}>
                            {submissionStatus.msg}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>取消</Button>
                    <Button onClick={handleConfirmSubmit} variant="contained">确认提交</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>确认删除字幕</DialogTitle>
                <DialogContent>
                    <Typography>
                        您确定要删除 Clip ID: <strong>{clipToDelete?.id}</strong> 的字幕吗？此操作不可撤销。
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>取消</Button>
                    <Button onClick={handleConfirmDelete} color="error">确认删除</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SubtitlePage;
